import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { clienteService } from "@/services/api/cliente.service";
import {
  SetClienteToClienteFilialPayload,
  UpdateClienteFilialPayload,
} from "@/types/cliente-filial.type";

export function useFilialActions() {
  const queryClient = useQueryClient();

  const invalidateQueriesGlobally = () => {
    queryClient.invalidateQueries({ queryKey: ["filiais-list"] });
    queryClient.invalidateQueries({ queryKey: ["filiais-pendentes-list"] });
    queryClient.invalidateQueries({ queryKey: ["clientes-matriz-list"] });
    queryClient.invalidateQueries({ queryKey: ["clientes-resumo-totais"] });
  };

  const onSuccessAction = (mensagem: string) => {
    invalidateQueriesGlobally();
    toast.success(mensagem);
  };

  const onErrorAction = (error: any, defaultMessage: string) => {
    console.error(defaultMessage, error);
  };

  const vincularClienteMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: SetClienteToClienteFilialPayload }) =>
      clienteService.setClienteToFilial(id, payload),
    onSuccess: () => onSuccessAction("Filial vinculada à matriz com sucesso!"),
    onError: (error) => onErrorAction(error, "Falha ao vincular a filial."),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: UpdateClienteFilialPayload }) =>
      clienteService.updateFilial(id, payload),
    onSuccess: () => onSuccessAction("Dados da filial atualizados!"),
    onError: (error) => onErrorAction(error, "Falha ao atualizar filial."),
  });

  const inactivateMutation = useMutation({
    mutationFn: (id: number) => clienteService.inactivateFilial(id),
    onSuccess: () => onSuccessAction("Filial inativada com sucesso!"),
    onError: (error) => onErrorAction(error, "Falha ao inativar filial."),
  });

  const reactivateMutation = useMutation({
    mutationFn: (id: number) => clienteService.reactivateFilial(id),
    onSuccess: () => onSuccessAction("Filial reativada com sucesso!"),
    onError: (error) => onErrorAction(error, "Falha ao reativar filial."),
  });

  return {
    isLinking: vincularClienteMutation.isPending,
    isUpdating: updateMutation.isPending,
    isTogglingStatus: inactivateMutation.isPending || reactivateMutation.isPending,
    vincularCliente: vincularClienteMutation.mutateAsync,
    updateFilial: updateMutation.mutateAsync,
    inactivateFilial: inactivateMutation.mutateAsync,
    reactivateFilial: reactivateMutation.mutateAsync,
  };
}
